import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebaseConfig';
import { useAuth } from './hooks/useAuth';
import LoginForm from './components/LoginForm';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './DashboardPage.css';

const DashboardPage = () => {
  const { user, loading, error: authError, login, logout } = useAuth();
  const [loginError, setLoginError] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [metricsError, setMetricsError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    if (user) {
      fetchMetrics();
    }
  }, [user]);

  const fetchMetrics = async () => {
    setIsLoadingMetrics(true);
    setMetricsError(null);

    try {
      const getMetrics = httpsCallable(functions, 'getMetrics');
      const result = await getMetrics();

      if (result.data.success) {
        setMetrics(result.data.metrics);
        setLastUpdated(new Date());
      } else {
        throw new Error('Failed to fetch metrics');
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
      console.error('User email:', user?.email);

      let errorMessage = 'Failed to load metrics. Please try again.';

      if (error.code === 'functions/permission-denied') {
        errorMessage = 'You do not have permission to access this dashboard. Please contact the administrator.';
      } else if (error.code === 'functions/unauthenticated') {
        errorMessage = 'Authentication failed. Please sign in again.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setMetricsError(errorMessage);
      setMetrics(null); // Clear any existing metrics on error
    } finally {
      setIsLoadingMetrics(false);
    }
  };

  const handleRefresh = () => {
    fetchMetrics();
  };

  const handleLogin = async (email, password) => {
    // Check if email is authorized before attempting login
    const authorizedEmail = 'salomeranson@gmail.com';
    if (email !== authorizedEmail) {
      setLoginError('You do not have permission to access this dashboard.');
      return;
    }

    setLoginError(null);
    try {
      await login(email, password);
    } catch (error) {
      setLoginError(error.message);
    }
  };

  // Show loading state during initial auth check
  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="container">
          <div className="loading-message">Loading...</div>
        </div>
      </div>
    );
  }

  // Show login form if user is not authenticated
  if (!user) {
    return (
      <div className="dashboard-page">
        <div className="container">
          <div className="dashboard-login-header">
            <h1>NextLink Dashboard</h1>
            <p>Sign in to view metrics</p>
          </div>
          <LoginForm
            onLogin={handleLogin}
            error={loginError || authError}
            loading={loading}
            title="Sign In"
            subtitle="Please sign in to access the dashboard."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="container">
        <header className="dashboard-header">
          <div className="header-content">
            <h1>NextLink Dashboard</h1>
            <div className="header-actions">
              <button onClick={handleRefresh} className="btn-refresh" disabled={isLoadingMetrics}>
                {isLoadingMetrics ? 'Refreshing...' : 'Refresh'}
              </button>
              <button onClick={logout} className="btn-logout">
                Sign Out
              </button>
            </div>
          </div>
          {lastUpdated && (
            <p className="last-updated">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
          )}
        </header>

        {metricsError && (
          <div className="error-banner">
            {metricsError}
          </div>
        )}

        {isLoadingMetrics && !metrics ? (
          <div className="loading-metrics">
            <div className="spinner"></div>
            <p>Loading metrics...</p>
          </div>
        ) : metrics ? (
          <>
            {/* Core Metrics */}
            <section className="metrics-section">
              <h2 className="section-title">Overview</h2>
              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-icon users-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                  </div>
                  <div className="metric-content">
                    <h3 className="metric-label">Total Users</h3>
                    <p className="metric-value">{metrics.totalUsers?.toLocaleString() || '0'}</p>
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-icon groups-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7"></rect>
                      <rect x="14" y="3" width="7" height="7"></rect>
                      <rect x="14" y="14" width="7" height="7"></rect>
                      <rect x="3" y="14" width="7" height="7"></rect>
                    </svg>
                  </div>
                  <div className="metric-content">
                    <h3 className="metric-label">Total Groups</h3>
                    <p className="metric-value">{metrics.totalGroups?.toLocaleString() || '0'}</p>
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-icon messages-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                  </div>
                  <div className="metric-content">
                    <h3 className="metric-label">Total Messages</h3>
                    <p className="metric-value">{metrics.totalMessages?.toLocaleString() || '0'}</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Growth Charts */}
            {metrics.timeSeriesData && metrics.timeSeriesData.length > 0 && (
              <section className="metrics-section">
                <h2 className="section-title">Growth Over Time (Last 30 Days)</h2>
                <div className="charts-container">
                  <div className="chart-card">
                    <h3 className="chart-title">New Users</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={metrics.timeSeriesData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="users" stroke="#667eea" strokeWidth={2} name="New Users" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="chart-card">
                    <h3 className="chart-title">New Groups</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={metrics.timeSeriesData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="groups" stroke="#f5576c" strokeWidth={2} name="New Groups" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="chart-card">
                    <h3 className="chart-title">Messages</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={metrics.timeSeriesData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="messages" stroke="#00f2fe" strokeWidth={2} name="Messages" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </section>
            )}

            {/* Growth Metrics */}
            <section className="metrics-section">
              <h2 className="section-title">Growth</h2>
              <div className="metrics-grid">
                <div className="metric-card small">
                  <div className="metric-content">
                    <h3 className="metric-label">New Users (7d)</h3>
                    <p className="metric-value">{metrics.newUsersLast7Days?.toLocaleString() || '0'}</p>
                  </div>
                </div>

                <div className="metric-card small">
                  <div className="metric-content">
                    <h3 className="metric-label">New Users (30d)</h3>
                    <p className="metric-value">{metrics.newUsersLast30Days?.toLocaleString() || '0'}</p>
                  </div>
                </div>

                <div className="metric-card small">
                  <div className="metric-content">
                    <h3 className="metric-label">New Groups (7d)</h3>
                    <p className="metric-value">{metrics.newGroupsLast7Days?.toLocaleString() || '0'}</p>
                  </div>
                </div>

                <div className="metric-card small">
                  <div className="metric-content">
                    <h3 className="metric-label">New Groups (30d)</h3>
                    <p className="metric-value">{metrics.newGroupsLast30Days?.toLocaleString() || '0'}</p>
                  </div>
                </div>

                <div className="metric-card small">
                  <div className="metric-content">
                    <h3 className="metric-label">Messages (7d)</h3>
                    <p className="metric-value">{metrics.messagesLast7Days?.toLocaleString() || '0'}</p>
                  </div>
                </div>

                <div className="metric-card small">
                  <div className="metric-content">
                    <h3 className="metric-label">Messages (30d)</h3>
                    <p className="metric-value">{metrics.messagesLast30Days?.toLocaleString() || '0'}</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Engagement Metrics */}
            <section className="metrics-section">
              <h2 className="section-title">Engagement</h2>
              <div className="metrics-grid">
                <div className="metric-card small">
                  <div className="metric-content">
                    <h3 className="metric-label">Daily Active Users</h3>
                    <p className="metric-value">{metrics.dailyActiveUsers?.toLocaleString() || '0'}</p>
                  </div>
                </div>

                <div className="metric-card small">
                  <div className="metric-content">
                    <h3 className="metric-label">Weekly Active Users</h3>
                    <p className="metric-value">{metrics.weeklyActiveUsers?.toLocaleString() || '0'}</p>
                  </div>
                </div>

                <div className="metric-card small">
                  <div className="metric-content">
                    <h3 className="metric-label">Monthly Active Users</h3>
                    <p className="metric-value">{metrics.monthlyActiveUsers?.toLocaleString() || '0'}</p>
                  </div>
                </div>

                <div className="metric-card small">
                  <div className="metric-content">
                    <h3 className="metric-label">Avg Messages/User</h3>
                    <p className="metric-value">{metrics.avgMessagesPerUser?.toLocaleString() || '0'}</p>
                  </div>
                </div>

                <div className="metric-card small">
                  <div className="metric-content">
                    <h3 className="metric-label">Avg Messages/Group</h3>
                    <p className="metric-value">{metrics.avgMessagesPerGroup?.toLocaleString() || '0'}</p>
                  </div>
                </div>

                <div className="metric-card small">
                  <div className="metric-content">
                    <h3 className="metric-label">Avg Group Size</h3>
                    <p className="metric-value">{metrics.avgGroupSize?.toLocaleString() || '0'}</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Feature Usage Metrics */}
            <section className="metrics-section">
              <h2 className="section-title">Feature Usage</h2>
              <div className="metrics-grid">
                <div className="metric-card small feature-card">
                  <div className="metric-content">
                    <h3 className="metric-label">Quick Forms</h3>
                    <p className="metric-value">{metrics.totalQuickForms?.toLocaleString() || '0'}</p>
                  </div>
                </div>

                <div className="metric-card small feature-card">
                  <div className="metric-content">
                    <h3 className="metric-label">Quick Polls</h3>
                    <p className="metric-value">{metrics.totalQuickPolls?.toLocaleString() || '0'}</p>
                  </div>
                </div>

                <div className="metric-card small feature-card">
                  <div className="metric-content">
                    <h3 className="metric-label">Events</h3>
                    <p className="metric-value">{metrics.totalEvents?.toLocaleString() || '0'}</p>
                  </div>
                </div>

                <div className="metric-card small feature-card">
                  <div className="metric-content">
                    <h3 className="metric-label">Payment Requests</h3>
                    <p className="metric-value">{metrics.totalPaymentRequests?.toLocaleString() || '0'}</p>
                  </div>
                </div>

                <div className="metric-card small feature-card">
                  <div className="metric-content">
                    <h3 className="metric-label">Sign-Up Sheets</h3>
                    <p className="metric-value">{metrics.totalSignUpSheets?.toLocaleString() || '0'}</p>
                  </div>
                </div>
              </div>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default DashboardPage;
